<template>
    <figure
    :class="classes"
    @mouseenter="$emit('mouseenter', $event)"
    @mouseleave="$emit('mouseleave', $event)"
    @click="$emit('click')">
        <div
        ref="img_ratio"
        :data-figure-alt="alt"
        :data-figure-src="src"
        :class="img_ratio_classes"
        :style="img_ratio_styles">
            <span v-if="hasSrc && !hasLazySrc && loading">loading  ...</span>
        </div>

        <img
        ref="img"
        :class="img_classes"
        :src="img_src"
        :alt="alt"
        loading="lazy"
        @load="handleImgLoad" />

        <div
        v-if="overlay"
        class="overlay figure--overlay"></div>

        <div
        v-if="hasDefaultSlot"
        class="figure--content">
            <slot name="content" />
        </div>

        <div
        v-if="!hasLazySrc && loading"
        class="figure--loading">
            <slot name="loading" />
        </div>

        <slot />
    </figure>
</template>

<script>
import { ratio } from '@/mixins'

export default {
    name: 'figure-comp',
    mixins: [ratio],
    props: {
        overlay: {
            type: Boolean,
            required: false,
            default: false
        },
        link: {
            type: Boolean,
            required: false,
            default: false
        },
        img: {
            type: Boolean,
            required: false,
            default: false
        },
        //
        'background-position': { // x y
            type: String,
            required: false,
            default: 'center'
        },
        //
        src: {
            type: String,
            required: false,
            default: null
        },
        alt: {
            type: String,
            required: false,
            default: null
        },
        //
        lazy: {
            type: Boolean,
            required: false,
            default: true
        },
        'lazy-src': {
            type: String,
            required: false,
            default: null
        },
        'lazy-delay': {
            type: Number,
            required: false,
            default: 0
        },
        //
        'no-src': {
            type: String,
            required: false,
            default: null
        },
        'error-src': {
            type: String,
            required: false,
            default: null
        }
    },
    computed: {
        hasDefaultSlot () {
            return 'default' in this.$slots
        },
        hasSrc () {
            return this.cacheSrc !== null || (this.cacheSrc !== null && typeof this.cacheSrc === 'string' && this.cacheSrc.length > 0)
        },
        hasLazySrc () {
            return this.cache_lazy_src !== null || (this.cache_lazy_src !== null && typeof this.cache_lazy_src === 'string' && this.cache_lazy_src.length > 0)
        },
        hasNoSrc () {
            return this.cache_no_src !== null || (this.cache_no_src !== null && typeof this.cache_no_src === 'string' && this.cache_no_src.length > 0)
        },
        hasErrorSrc () {
            return this.cache_error_src !== null || (this.cache_error_src !== null && typeof this.cache_error_src === 'string' && this.cache_error_src.length > 0)
        },
        //
        classes () {
            let v = {
                figure: true,
                'figure-link': this.link,
                'figure-loading': this.loading,
                'figure-loaded': this.loaded,
                'figure-loaded-error': this.loaded_error,
                'figure-empty': !this.hasSrc,
                'figure-loading-lazy': this.loading && this.hasLazySrc
            }

            if (!!this.ratio && !this.cache_img) {
                v = {
                    ...v,
                    ...this.ratioClasses
                }
            }

            return v
        },
        //
        img_classes () {
            return { hidden: !this.cache_img }
        },
        img_src () {
            if (this.loading) {
                if (this.hasLazySrc) return this.cache_lazy_src
            }

            if (this.loaded) {
                if (this.loaded_error) {
                    if (this.hasErrorSrc) return this.cache_error_src
                }

                if (!this.hasSrc && this.hasNoSrc) return this.cache_no_src
            }

            return this.cacheSrc
        },
        //
        img_ratio_classes () {
            return ['figure--div', {
                'r-c': !!this.ratio && !this.cache_img,
                hidden: this.cache_img && !!this.ratio
            }]
        },
        img_ratio_styles () {
            const v = {
                backgroundPosition: this.backgroundPosition,
                backgroundImage: `url(${this.img_src})`
            }

            return v
        }
    },
    data () {
        return {
            key: 0,
            cacheSrc: this.src,
            cache_lazy_src: this.lazySrc,
            cache_no_src: this.noSrc,
            cache_error_src: this.errorSrc,
            //
            w: 0,
            h: 0,
            //
            cache_img: this.img || !this.ratio,
            //
            loading: false,
            loaded: false,
            loaded_error: false
        }
    },
    created () {
        this.start()
    },
    mounted () {
        this.$nextTick(this.play)
    },
    watch: {
        src (newVal) {
            this.key += 1
            this.cacheSrc = this.newVal
            this.preload()
        },
        noSrc (newVal) {
            this.cache_no_src = newVal
        },
        errorSrc (newVal) {
            this.cache_error_src = newVal
        },
        img (newVal) {
            this.cache_img = newVal
        }
    },
    methods: {
        start () {
            this.loading = this.lazy && this.hasSrc
            this.loaded = !this.hasSrc
        },
        play () {
            if (this.lazy) this.preload()
        },
        handleImgLoad (evt) {
            // console.log('img loaded', evt)
            this.$emit('img-load', this.cacheSrc)
        },
        preload () {
            if (!this.hasSrc) return

            this.loading = true
            this.loaded_error = false

            this.$emit('loading')

            return new Promise((resolve, reject) => {
                // console.log('preload', this.cacheSrc)

                const image = new Image()
                image.src = this.cacheSrc

                if (image.complete) {
                    this.w = this.$refs.img.clientWidth
                    this.h = this.$refs.img.clientHeight

                    this.loaded = true
                    this.loading = false

                    this.$emit('loaded')
                    resolve({ img: image, width: this.w, height: this.h })
                }

                image.onload = () => {
                    this.w = this.cache_img ? this.$refs.img.clientWidth : this.$refs.img_ratio.clientWidth
                    this.h = this.cache_img ? this.$refs.img.clientHeight : this.$refs.img_ratio.clientHeight

                    setTimeout(() => {
                        this.loaded = true
                        this.loading = false

                        this.$emit('loaded')
                        resolve({ img: image, width: this.w, height: this.h })
                    }, this.lazyDelay)
                }

                image.onerror = () => {
                    setTimeout(() => {
                        this.loaded_error = true
                        this.loaded = true
                        this.loading = false

                        this.$emit('loaded-error')
                        resolve({ img: image })
                    }, this.lazyDelay)
                }
            })
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/mixins.scss";

.figure {
    position: relative;
    display: block;
    width: 100%;
    background-color: rgb(230, 230, 230);

    &.figure-loading {
        > .figure--div {
        }
    }

    &.figure-loading-lazy {
    }

    &.figure-loaded {
        > .figure--div {
        }
    }

    &.figure-empty {
        > .figure--div {
        }
    }

    &.figure-link {
        cursor: pointer;
        overflow: hidden;

        .figure--div {
            transition: transform 0.3s ease-out;
        }

        &:hover {
            .figure--div {
                transform: scale(1.1);
            }
        }
    }

    &--div {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;

        display: flex;
        flex-flow: column nowrap;
        justify-content: center;
        align-items: center;

        background-size: cover;
        background-repeat: no-repeat;

        z-index: 1;
    }

    > img {
        z-index: 1;
    }

    &--overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-position: center center;
        background-repeat: no-repeat;
        background-size: cover;
        pointer-events: none;
        z-index: 2;

        &::before,
        &::after {
            content: "";
            position: absolute;
            left: 0;
            width: 100%;
            height: 50%;
            background-position: center center;
            background-repeat: no-repeat;
            background-size: cover;
        }

        &::before {
            top: 0;
            background-image: linear-gradient(to bottom, rgba(#000, 0.4), rgba(#fff, 0));
        }

        &::after {
            bottom: 0;
            background-image: linear-gradient(to top, rgba(#000, 0.8), rgba(#fff, 0));
        }
    }

    &--content {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 3;
    }

    &--loading {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: var(--color-mute);

        display: flex;
        flex-flow: column nowrap;
        justify-content: center;

        text-align: center;

        z-index: 4;
    }
}

.hidden {
    position: absolute;
    overflow: hidden;
    clip: rect(0 0 0 0);
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    word-wrap: normal;
}
</style>